import {
  CollectionsFetchDocument,
  CollectionsFetchQuery,
  CollectionsFetchQueryVariables,
  useCollectionCreateMutation,
  useSettingsQuery,
  useSetupOrganisationMutation,
} from 'lib-knowledge-management-common/graphql';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

type MutationFunction = ReturnType<typeof useCollectionCreateMutation>[0];
type MutationReturnValue = ReturnType<MutationFunction>;
type CreateCollectionFn = (data: {
  title: string;
  internalKbEnabled: boolean;
  internalOnly?: boolean;
}) => MutationReturnValue;

type UseCollectionCreateReturnValue = [
  CreateCollectionFn,
  ReturnType<typeof useCollectionCreateMutation>[1],
];

/**
 * React hook that returns a function for creating a collection and the result
 * of the create mutation. If the user is allowed to create collections and the
 * organisation is not set up yet, the returned function will first call the
 * setup organisation mutation before creating the collection.
 *
 * @returns {UseCollectionCreateReturnValue} - An array with the create
 *   collection function and the result of the collection create mutation.
 */
function useCollectionCreate(): UseCollectionCreateReturnValue {
  const [createMutation, collectionCreateMutationResult] =
    useCollectionCreateMutation();
  const [setupOrganisationMutation, setupOrganisationMutationResult] =
    useSetupOrganisationMutation();
  const settings = useSettingsQuery({ fetchPolicy: 'cache-first' });
  const createCollection: CreateCollectionFn = useCallback(
    ({ title, internalKbEnabled, internalOnly }) => {
      return createMutation({
        variables: {
          title,
          internalKbEnabled,
          internalOnly,
        },
        update: (cache, { data }) => {
          if (!data || !data.collectionCreate) return;

          const collectionsList = cache.readQuery<
            CollectionsFetchQuery,
            CollectionsFetchQueryVariables
          >({
            query: CollectionsFetchDocument,
          });

          if (!collectionsList) return;

          cache.writeQuery<
            CollectionsFetchQuery,
            CollectionsFetchQueryVariables
          >({
            query: CollectionsFetchDocument,
            data: {
              __typename: 'Queries',
              collections: {
                __typename: 'CollectionList',
                results: [
                  ...collectionsList.collections.results,
                  {
                    __typename: 'CollectionListItem',
                    id: data.collectionCreate.id,
                    title: data.collectionCreate.title,
                    internalKbEnabled: data.collectionCreate.internalKbEnabled,
                    internalOnly: data.collectionCreate.internalOnly,
                    isSyncedWithElevio:
                      data.collectionCreate.isSyncedWithElevio,
                  },
                ],
              },
              settings: {
                __typename: 'SettingsPayload',
                canCreateCollections:
                  collectionsList.settings.canCreateCollections,
                isOrganisationSetup:
                  collectionsList.settings.isOrganisationSetup,
              },
            },
          });
        },
      }).then((args) => {
        ampli.knowledgeCollectionCreated({
          collectionId: args.data?.collectionCreate?.id || 'unknown',
        });
        return args;
      });
    },
    [createMutation],
  );

  /** First sets up an organisation, then calls createCollection */
  const setupOrganisation: CreateCollectionFn = useCallback(
    (data) => {
      return setupOrganisationMutation().then(() => createCollection(data));
    },
    [createCollection, setupOrganisationMutation],
  );

  if (setupOrganisationMutationResult.error) {
    throw new Error('There was an error setting up an organisation');
  }

  if (!settings.data || settings.error) {
    throw new Error('There was an error fetching settings');
  }

  const { canCreateCollections, isOrganisationSetup } = settings.data.settings;

  const setupOrganisationRequired =
    canCreateCollections && !isOrganisationSetup;

  const createFunction = setupOrganisationRequired
    ? setupOrganisation
    : createCollection;

  return [createFunction, collectionCreateMutationResult];
}

export default useCollectionCreate;
